import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooterCompact from "@/components/landing/SiteFooterCompact";
import { POSTS } from "@/pages/blog/posts";
import { ArrowRight, Clock } from "lucide-react";

export default function Blog() {
  useEffect(() => {
    document.title = "Blog — Tradecure | Insights for Indian retail traders";
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="blog-index" className="bg-white text-[#0f172a]">
      <SiteHeader />

      {/* Hero */}
      <section className="relative pt-16 pb-12 md:pt-20 md:pb-16 border-b border-[#e2e8f0] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-mono text-xs font-bold text-[#084392] tracking-[0.22em]">
              / BLOG
            </div>
            <h1 className="font-heading mt-4 text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98]">
              Sharp takes.
              <br />
              <span className="text-[#084392]">Zero fluff.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-[#475569]">
              Field notes from the Indian markets — what works for the 90%
              retail trader, what doesn&rsquo;t, and why.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="mx-auto max-w-7xl px-5 md:px-8 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {POSTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            >
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="block rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden hover:border-[#084392]/50 hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
              >
                <div
                  className="h-44 relative overflow-hidden"
                  style={{ background: p.cover }}
                >
                  <div className="absolute inset-0 dot-bg opacity-30" />
                  <div className="absolute bottom-3 left-4 text-white/80 font-mono text-[10px] font-bold tracking-[0.18em]">
                    {p.tag.toUpperCase()}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs font-mono text-[#475569]">
                    <span>{p.date}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock size={11} /> {p.readMins} min
                    </span>
                  </div>
                  <h2 className="font-heading mt-3 text-xl font-bold tracking-tight leading-snug">
                    {p.title}
                  </h2>
                  <p className="mt-2 text-[#475569] text-sm leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#084392]">
                    Read article <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <SiteFooterCompact />
    </main>
  );
}
